import React, {SyntheticEvent, useEffect, useState} from 'react';

import {
  Breakpoints,
  Checkbox,
  CTA,
  Select,
  styled,
  TextInput,
  Richtext,
  Text,
  TokenTextAppearance
} from '@volkswagen-onehub/components-core';
import {useAppDispatch} from '../../store/hooks';
import {set, setFromSkip} from '../../store/features/pages/pageSlice';
import {Page} from '../../types/enums/page.enum';
import {TAOS, TIGUAN, ATLAS, ATLASCROSSPORT} from '../../data/models';
import {LeadService} from '../../services/LeadService';
import useTrans from '../../hooks/use-trans';
import {CTAWrapper} from '../../styles/global.style';
import {useFeatureServices} from '../../use-feature-services';
import {Language} from '../../types/locale';
import {isValidEmail, validatePostalCode} from '../../validation';
import questions from '../../data/questions';

import { FormErrorField } from '../../types/tracking';

const FIELD_LABELS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  model: 'Model',
  postal: 'Postal Code',
  optin: 'Opt-in',
};

const ADDRESS_FIELDS = new Set(['email','postal']);

function buildErrorFields(errors: Partial<Record<string, string>>) {
  const errorFields: FormErrorField[] = [];
  const addressFields: FormErrorField[] = [];

  Object.keys(errors).forEach((key) => {
    const field: FormErrorField = {
      [key]: FIELD_LABELS[key] ?? key,
    };
    if (ADDRESS_FIELDS.has(key)) {
      addressFields.push(field);
    } else {
      errorFields.push(field);
    }
  });

  return { errorFields, addressFields };
}

const FormsContainer = styled.form`
  width: 100%;

  @media screen and (min-width: ${Breakpoints.b960}px) {
    padding: 0 ${(props) => props.theme.size.grid001};
  }
`;

const FieldWrapper = styled.div`
  flex: 1 1 100%;
`;

const SectionContainer = styled.div`
  flex: 1 1 100%;
  display: flex;
  flex-wrap: wrap;
  row-gap: ${(props) => props.theme.size.static250};
`;

const FieldsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.size.static350};
`;

const SectionCard = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  background-color: ${(props) => props.theme.colors.background.tertiary};
  box-shadow: ${(props) => props.theme.colors.shadow.default};
  border-radius: ${(props) => props.theme.size.borderRadius.cardL};
  padding: ${(props) => props.theme.size.grid001};
  display: flex;
  flex-wrap: wrap;
  row-gap: ${(props) => props.theme.size.static400};
`;

const SectionSubHead = styled(Text)`
  width: 100%;
  margin: 0;
`;

const ActionsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${(props) => props.theme.size.dynamic0100};
  @media screen and (min-width: 480px) {
    flex-direction: row;
  }
`;

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  model: string;
  postal: string;
  optin: string;
};

export default function () {
  const {'locale-service': localeService} = useFeatureServices();

  const {tracking} = useFeatureServices();

  const trans = useTrans();

  const enTrans = useTrans('en' as Language);

  const dispatch = useAppDispatch();

  const totalSteps = Object.keys(questions).length;
  const formStepNumber = totalSteps + 1;

  const [values, setValues] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    model: '',
    postal: '',
    optin: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  const [requesting, setRequesting] = useState<boolean>(false);

  useEffect(() => {
    tracking.track('VWBasic_Form_Pageload', {
      eventInfo: {
        brand: "passenger"
      },
      form: {
        FormTypeMultiple: ['newsletter'],
        FormName: 'SUVW Lead Capture Form',
        FormVersion: '1',
        FormStart: true,
      },
      process: {
        Name: 'SUVW Questionnaire and Lead Capture Form',
        StepName: 'Lead Capture Form',
        StepNumber: `${formStepNumber}`,
      },
    });
  }, []);

  const update = (
    e:
      | SyntheticEvent<HTMLInputElement, Event>
      | SyntheticEvent<HTMLSelectElement, Event>,
  ): void => {

    const {name, value} = e.currentTarget;

    if(name != 'optin') {
      setValues((prev) => ({...prev, [name]: value}));
    }
    else {
      const optinValue = (e.currentTarget as HTMLInputElement).checked ? 'true' : '';
      setValues((prev) => ({...prev, [name]: optinValue}));
    }

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({...prev, [name]: undefined}));
    }
  };

  const validate = () => {
    const newErrors: Partial<FormData> = {};

    if (!values.firstName.trim())
      newErrors.firstName = trans.pages.forms.errors.firstName;
    if (!values.lastName.trim())
      newErrors.lastName = trans.pages.forms.errors.lastName;
    if (!values.email.trim()) {
      newErrors.email = trans.pages.forms.errors.email;
    } else if (!isValidEmail(values.email)) {
      newErrors.email = trans.pages.forms.errors.email;
    }
    if (!values.postal.trim()) {
      newErrors.postal = trans.pages.forms.errors.postal;
    } else if (!validatePostalCode(values.postal)) {
      newErrors.postal = trans.pages.forms.errors.postal;
    }
    if (!values.model.trim()) newErrors.model = trans.pages.forms.errors.model;
    if (!values.optin.trim()) newErrors.optin = trans.pages.forms.errors.optin;
    return newErrors;
  };

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    tracking.track('VWBasic_FormCTA_Click', {
      eventInfo: {
        contentId: 'SUVW Lead Capture Form - Submit',
        brand: "passenger"
      },
      form: {
        FormTypeMultiple: ['newsletter'],
        FormName: 'SUVW Lead Capture Form',
        FormVersion: '1',
        FormStart: false
      },
      process: {
        Name: 'SUVW Questionnaire and Lead Capture Form',
        StepName: 'Lead Capture Form',
        StepNumber: `${formStepNumber}`,
      },
      link: {
        url: window.location.href,
        name: enTrans.pages.forms.submit,
      },
    });

    const errors = validate();
    setErrors(errors);

    if (Object.keys(errors).length === 0) {
      try {
        setRequesting(true);

        await LeadService.sendLead(values, localeService.language);

        setValues({
          firstName: '',
          lastName: '',
          email: '',
          model: '',
          postal: '',
          optin: '',
        });

        setErrors({});
        dispatch(set(Page.RESULT));

        setRequesting(false);

        tracking.track('VWBasic_FormSubmissionSuccessMessage_Load', {
          eventInfo: {
            contentId: 'SUVW Lead Capture Form - Success',
            brand: "passenger"
          },
          form: {
            FormTypeMultiple: ['newsletter'],
            FormName: 'SUVW Lead Capture Form',
            FormVersion: '1',
            FormStart: false,
          },
          process: {
            Name: 'SUVW Questionnaire and Lead Capture Form',
            StepName: 'Lead Capture Form - Success',
            StepNumber: `${formStepNumber}`,
          },
          link: {
            url: window.location.href,
            name: enTrans.pages.forms.submit,
          },
        });
      } catch (err: any) {
        setRequesting(false);

        const { errorFields } = buildErrorFields({});

        tracking.track('VWBasic_FormErrorMessage_Load', {
          eventInfo: {
            contentId: 'SUVW Lead Capture Form - Submission Error',
            brand: "passenger"
          },
          form: {
            FormName: 'SUVW Lead Capture Form',
            FormVersion: '1',
            ErrorFormFields: errorFields,
            FormStart: false
          },
          process: {
            Name: 'SUVW Questionnaire and Lead Capture Form',
            StepName: 'Lead Capture Form - Error',
            StepNumber: `${formStepNumber}`,
          },
          error: {
            code: err?.code,
            message: err?.message,
            referringUrl: window.location.href,
          },
          link: {
            url: window.location.href,
            name: `${enTrans.pages.forms.submit}`,
          },
        });
      }
    } else if (Object.keys(errors).length > 0) {
      const { errorFields } = buildErrorFields(errors);

      tracking.track('VWBasic_FormErrorMessage_Load', {
          eventInfo: {
            contentId: 'SUVW Lead Capture Form - Validation Error',
            brand: "passenger"
          },
          form: {
            FormName: 'SUVW Lead Capture Form',
            FormVersion: '1',
            ErrorFormFields: errorFields,
          },
          process: {
            Name: 'SUVW Questionnaire and Lead Capture Form',
            StepName: 'Lead Capture Form - Error',
            StepNumber: `${formStepNumber}`,
          },
          error: {
            message: Object.values(errors).filter(Boolean).join('; '),
            referringUrl: window.location.href,
          },
          link: {
            url: window.location.href,
            name: `${enTrans.pages.forms.submit}`,
          },
        });
    }
  }

  const handleSkip = () => {
    dispatch(setFromSkip(Page.RESULT));

    tracking.track('VWBasic_Button_Click', {
      eventInfo: {
        contentId: 'SUVW Lead Capture Form - Skip',
        brand: "passenger"
      },
      process: {
        Name: 'SUVW Questionnaire and Lead Capture Form',
        StepName: 'Lead Capture Form - Skip',
        StepNumber: `${formStepNumber}`,
      },
      link: {
        url: window.location.href,
        name: enTrans.pages.forms.skip,
      },
    });
  };

  return (
    <FormsContainer onSubmit={handleSubmit}>
      <SectionCard>
        <SectionContainer>
          <SectionSubHead appearance={TokenTextAppearance.bigcopy100}>{trans.pages.forms.subheads.contact}</SectionSubHead>
          <FieldsContainer>
            <FieldWrapper>
              <TextInput
                appearance={errors.firstName ? 'error' : undefined}
                label={trans.pages.forms.fields.firstName}
                message={errors.firstName}
                name="firstName"
                onChange={(e) => update(e)}
              />
            </FieldWrapper>

            <FieldWrapper>
              <TextInput
                appearance={errors.lastName ? 'error' : undefined}
                label={trans.pages.forms.fields.lastName}
                message={errors.lastName}
                name="lastName"
                onChange={(e) => update(e)}
              />
            </FieldWrapper>

            <FieldWrapper>
              <TextInput
                appearance={errors.email ? 'error' : undefined}
                label={trans.pages.forms.fields.email}
                message={errors.email}
                name="email"
                onChange={(e) => update(e)}
              />
            </FieldWrapper>

            <FieldWrapper>
              <TextInput
                appearance={errors.postal ? 'error' : undefined}
                label={trans.pages.forms.fields.postal}
                message={errors.postal}
                name="postal"
                onChange={(e) => update(e)}
              />
            </FieldWrapper>
          </FieldsContainer>
        </SectionContainer>

        <SectionContainer>
          <SectionSubHead appearance={TokenTextAppearance.bigcopy100}>{trans.pages.forms.subheads.vehicle}</SectionSubHead>
          <FieldsContainer>
            <FieldWrapper>
              <Select
                message={errors.model}
                appearance={errors.model ? ('error' as any) : (undefined as any)}
                name="model"
                label={trans.pages.forms.fields.model}
                onChange={(e) => update(e)}
                defaultValue="default"
              >
                <option value="default" hidden>
                  {trans.pages.forms.fields.modelPlaceholder}
                </option>
                <option value={TAOS.code}>{TAOS.modelName}</option>
                <option value={TIGUAN.code}>{TIGUAN.modelName}</option>
                <option value={ATLASCROSSPORT.code}>
                  {ATLASCROSSPORT.modelName}
                </option>
                <option value={ATLAS.code}>{ATLAS.modelName}</option>
              </Select>
            </FieldWrapper>
          </FieldsContainer>
        </SectionContainer>

        <SectionContainer>
          <SectionSubHead appearance={TokenTextAppearance.bigcopy100}>{trans.pages.forms.subheads.marketingPermissions}</SectionSubHead>
          <FieldWrapper>
            <Checkbox
              onChange={(e) => update(e)}
              name="optin"
              value="true"
              label={<Richtext markdown={trans.pages.forms.fields.optin} dangerouslyParseRawHTML />}
              appearance={errors.optin ? ('error' as any) : (undefined as any)}
              message={errors.optin as unknown as string}
            />
          </FieldWrapper>
        </SectionContainer>

        <ActionsWrapper>
          <CTAWrapper>
            <CTA
              tag="button"
              emphasis="primary"
              type="submit"
              disabled={requesting}
            >
              {trans.pages.forms.submit}
            </CTA>
          </CTAWrapper>
          <CTAWrapper>
            <CTA
              tag="button"
              emphasis="secondary"
              type="button"
              onClick={handleSkip}
            >
              {trans.pages.forms.skip}
            </CTA>
          </CTAWrapper>
        </ActionsWrapper>
    </SectionCard>
  </FormsContainer>
  );
}
